import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from "react";
import { StyleSheet, View } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import {
  ChatMessagesList,
  ChatInputPanel,
  DisclaimerFooter,
  ErrorDisplay,
  ChatAnimation,
  Welcome,
  ScreenLayout,
  Spinner,
  withUnmountOnBlur,
  ChatSessionProvider,
  SessionSummary
} from "@/components";
import type { LiveKitTokenData } from "@/components";
import { ChatRoom, ConnectionState } from "@/components/ChatRoom";
import { useCrisisResources } from "@/hooks/crisis";
import {
  CrisisCooldownError,
  useSession,
  useSessionSummary
} from "@/hooks/session";
import { useAuth } from "@/hooks/auth";
import { useMessages } from "@/hooks/chat";
import { logger } from "@/utils";
import { Message } from "@/store/types";
import config from "@/config";
import { Timestamp } from "firebase/firestore";

function ChatScreen() {
  const { user } = useAuth();
  const router = useRouter();

  // Session state
  // sessionParam is the source of truth for session id. This component consumes
  // this source of truth and loads the session via context for downstream usage.
  const { session: sessionParam, pattern: patternParam } =
    useLocalSearchParams<{
      session?: string;
      pattern?: string;
    }>();
  const {
    sessionId,
    setSessionId,
    isArchived,
    isSessionLoading,
    loadSessionStore,
    createSession,
    getSession,
    autoConnectForSessionId,
    setAutoConnectForSessionId
  } = useSession();

  // Message management with streaming support
  const { messages, loadFromStore, appendMessage } = useMessages();

  // Session summary
  const { insights } = useSessionSummary(sessionId);

  // Token state
  const [tokenData, setTokenData] = useState<LiveKitTokenData | null>(null);

  // Input state
  const [inputText, setInputText] = useState("");

  // LiveKit state (provided by ChatRoom when connected)
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");
  const [error, setError] = useState<string | null>(null);
  const { openCooldown } = useCrisisResources();
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isInitialMountRef = useRef(true);
  // Guards the one-shot auto-start for pattern-focused sessions (below).
  const patternAutoStartRef = useRef(false);
  const [liveSend, setLiveSend] = useState<((text: string) => void) | null>(
    null
  );
  const [micToggle, setMicToggle] = useState<(() => void) | null>(null);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [speakerToggle, setSpeakerToggle] = useState<(() => void) | null>(null);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(false);

  // Filter messages for UI display (memoized to avoid recalculation on every render)
  const filteredMessages = useMemo(
    () =>
      filterMessagesForUI(
        messages,
        connectionState,
        isTimedOut,
        config.ui.showInputTranscript
      ),
    [messages, connectionState, isTimedOut]
  );

  useEffect(() => {
    logger.info("ChatScreen mounted");
    return () => {
      logger.info("ChatScreen unmounted");
    };
  }, []);

  // Load session for existing sessions using SessionContext.
  useEffect(() => {
    if (user && sessionParam) {
      // Has session parameter = existing session, load metadata via context
      loadSessionStore(sessionParam).catch((error) => {
        logger.error("Failed to load session", error);
      });
    } else if (user && !sessionParam) {
      // No session parameter = welcome screen, clear session context
      setSessionId(null);
    }
  }, [sessionParam, user, loadSessionStore, setSessionId]);

  // Load Firestore messages when sessionId changes
  useEffect(() => {
    if (!sessionId) return;

    loadFromStore(sessionId).catch((error) => {
      logger.error("Failed to load messages from Firestore", error);
    });
  }, [sessionId, loadFromStore]);

  // Add system message when connection state changes
  useEffect(() => {
    // Skip adding system message on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    const text = CONNECTION_STATE_MESSAGES[connectionState];
    if (text) {
      appendMessage({
        id: `system-${Date.now()}`,
        role: "system",
        text,
        timestamp: Timestamp.now()
      });
    }
  }, [connectionState, appendMessage]);

  // Callbacks for ChatRoom (memoized to prevent unnecessary re-renders)
  const handleNewMessage = useCallback(
    (msg: Message) => {
      appendMessage(msg);
    },
    [appendMessage]
  );

  const handleConnectionStateChange = useCallback((state: ConnectionState) => {
    setConnectionState(state);
  }, []);

  const handleSendHandle = useCallback((send: (text: string) => void) => {
    setLiveSend(() => send);
  }, []);

  const handleMicToggleHandle = useCallback((toggle: () => void) => {
    setMicToggle(() => toggle);
  }, []);

  const handleMicStateChange = useCallback((enabled: boolean) => {
    setIsMicEnabled(enabled);
  }, []);

  const handleSpeakerToggleHandle = useCallback((toggle: () => void) => {
    setSpeakerToggle(() => toggle);
  }, []);

  const handleSpeakerStateChange = useCallback((enabled: boolean) => {
    setIsSpeakerEnabled(enabled);
  }, []);

  const handleError = useCallback((errorMessage: string | null) => {
    setError(errorMessage);
  }, []);

  // Connection handlers
  const handleConnect = useCallback(async () => {
    setIsTimedOut(false);
    if (!sessionId) {
      // Create new session using context. A pattern param (from a Pattern
      // card) seeds a pattern-focused session; it is persisted server-side,
      // so we can drop it from the URL once the session exists.
      try {
        const newSessionId = await createSession(patternParam);
        router.replace(`/chat?session=${newSessionId}`);
        // Token will be fetched after navigation via auto-connect
        return;
      } catch (error) {
        if (error instanceof CrisisCooldownError) {
          logger.info("Session refused: crisis cooldown");
          openCooldown(error.body);
          return;
        }
        logger.error("Failed to create new session", error);
        setError(
          error instanceof Error ? error.message : "Failed to create session"
        );
        return;
      }
    }

    // Fetch token for existing session using context
    try {
      const result = await getSession(sessionId);
      setTokenData(result);
      setError(null);
    } catch (error) {
      if (error instanceof CrisisCooldownError) {
        logger.info("Session refused: crisis cooldown");
        openCooldown(error.body);
        return;
      }
      logger.error("Failed to fetch LiveKit token", error);
      setError(
        error instanceof Error ? error.message : "Failed to get session token"
      );
    }
  }, [
    sessionId,
    createSession,
    router,
    getSession,
    openCooldown,
    patternParam
  ]);

  // Auto-connect for newly created sessions
  useEffect(() => {
    if (sessionId && autoConnectForSessionId === sessionId) {
      logger.info(`Auto-connecting to session ${sessionId}`);
      setAutoConnectForSessionId(null); // Clear flag
      handleConnect();
    }
  }, [
    sessionId,
    autoConnectForSessionId,
    connectionState,
    setAutoConnectForSessionId,
    handleConnect
  ]);

  // Auto-start a pattern-focused session opened from a Pattern card
  // (/chat?pattern=...). The card tap is the user gesture, so we kick off the
  // same connect flow once instead of waiting for a second tap on Welcome.
  // Guarded to fire a single time, and only once the session context has
  // settled to null (so handleConnect takes the create-new-session path).
  useEffect(() => {
    if (
      user &&
      patternParam &&
      !sessionParam &&
      sessionId === null &&
      !patternAutoStartRef.current
    ) {
      patternAutoStartRef.current = true;
      logger.info("Auto-starting pattern-focused session");
      handleConnect();
    }
  }, [user, patternParam, sessionParam, sessionId, handleConnect]);

  const handleDisconnect = useCallback(() => {
    setTokenData(null); // Unmounts ChatSessionProvider
    setConnectionState("disconnected");
    // Messages remain in state
  }, []);

  // Auto-disconnect after SESSION_TIMEOUT_MS when session is ready
  useEffect(() => {
    if (connectionState !== "ready") return;

    const timer = setTimeout(() => {
      setIsTimedOut(true);
      appendMessage({
        id: `system-${Date.now()}`,
        role: "system",
        text: SESSION_TIMEOUT_MESSAGE,
        timestamp: Timestamp.now()
      });
      logger.error(SESSION_TIMEOUT_MESSAGE);
      handleDisconnect();
    }, SESSION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [connectionState, appendMessage, handleDisconnect]);

  const handleSend = () => {
    if (!liveSend) {
      logger.warn("LiveKit send function not ready");
      return;
    }
    const msg = inputText.trim();
    if (msg) {
      liveSend(msg);
    }
    setInputText("");
  };

  return (
    <ScreenLayout>
      <View style={styles.container}>
        {/* Conditional main content based on state */}
        {(() => {
          // Loading state - fetching existing session data
          if (isSessionLoading) {
            return <Spinner style={styles.loadingContainer} />;
          }

          // New session (no sessionParam) - show welcome immediately
          if (!sessionParam) {
            return <Welcome />;
          }

          return <ChatMessagesList messages={filteredMessages} />;
        })()}

        {/* Session Summary — rendered when disconnected; the component
            decides internally whether there's anything worth showing. */}
        {connectionState === "disconnected" && (
          <SessionSummary insights={insights} />
        )}

        {/* Error Display for connection errors */}
        <ErrorDisplay error={error} />

        {/* Relaxing Chat Animation - only show when ready */}
        {connectionState === "ready" && <ChatAnimation />}

        {/* Enhanced Chat Input Panel with Connection Controls */}
        {/* Show controls for non-archived sessions (new sessions and server-assigned sessions) */}
        {!isArchived && !isSessionLoading && (
          <ChatInputPanel
            inputText={inputText}
            onChangeText={setInputText}
            onSend={handleSend}
            connectionState={connectionState}
            isMicEnabled={isMicEnabled}
            isSpeakerEnabled={isSpeakerEnabled}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onToggleMic={micToggle || (() => {})}
            onToggleSpeaker={speakerToggle || (() => {})}
          />
        )}

        {/* ChatRoom only mounts when token exists */}
        {tokenData && (
          <ChatSessionProvider key={tokenData.livekitToken} token={tokenData}>
            <ChatRoom
              onMessage={handleNewMessage}
              onConnectionState={handleConnectionStateChange}
              onSendHandle={handleSendHandle}
              onMicToggleHandle={handleMicToggleHandle}
              onMicState={handleMicStateChange}
              onSpeakerToggleHandle={handleSpeakerToggleHandle}
              onSpeakerState={handleSpeakerStateChange}
              onError={handleError}
            />
          </ChatSessionProvider>
        )}

        <DisclaimerFooter />
      </View>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1
  },
  loadingContainer: {
    flex: 1
  }
});

const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const SESSION_TIMEOUT_MESSAGE =
  "Reached session limit of 60 minutes. You can resume the session anytime.";

/** Mapping of connection states to system messages */
const CONNECTION_STATE_MESSAGES: Record<ConnectionState, string | null> = {
  connecting: "Connecting...",
  connected: "Connecting to the AI assistant...",
  ready: null,
  reconnecting: "Reconnecting...",
  disconnected: "Disconnected."
};

/**
 * Filters chat messages for UI display based on configuration and connection state.
 * - Agent messages: Always shown
 * - User messages: Text input messages always shown; audio transcripts shown only if showInputTranscript config is enabled
 * - System messages: Only shows the most recent message matching current connection state
 *
 * @param allMessages - All messages from the chat session
 * @param connectionState - Current WebSocket connection state
 * @param showInputTranscript - Whether to show user audio transcript messages in UI
 * @returns Filtered array of messages for UI display
 */
function filterMessagesForUI(
  allMessages: Message[],
  connectionState: ConnectionState,
  isTimedOut: boolean,
  showInputTranscript: boolean
): Message[] {
  const currentSystemMessage = CONNECTION_STATE_MESSAGES[connectionState];

  return allMessages.filter((msg) => {
    if (msg.role === "system") {
      // Show timeout message only when the current disconnect was caused by timeout
      if (msg.text === SESSION_TIMEOUT_MESSAGE) {
        return isTimedOut && connectionState === "disconnected";
      }

      // Hide all system messages if no current state message
      if (!currentSystemMessage) return false;

      // Only show system messages that match current connection state
      if (msg.text !== currentSystemMessage) return false;

      // Find the most recent (last) system message with this text
      let lastMatchingIndex = -1;
      for (let i = allMessages.length - 1; i >= 0; i--) {
        if (
          allMessages[i].role === "system" &&
          allMessages[i].text === currentSystemMessage
        ) {
          lastMatchingIndex = i;
          break;
        }
      }

      // Show only if this is the most recent system message with current text
      const currentIndex = allMessages.indexOf(msg);
      return currentIndex === lastMatchingIndex;
    }

    if (msg.role === "user") {
      // Text input messages are always shown
      if (msg.isTextInput) {
        return true;
      }
      // Audio transcript messages are shown only if configured to do so
      return showInputTranscript;
    }

    return true; // Show all agent messages
  });
}

/**
 * Wrapper component that forces ChatScreen to remount when session changes
 * by using the session parameter as a React key prop
 */
function ChatWrapper() {
  const { session: sessionParam } = useLocalSearchParams<{
    session?: string;
  }>();

  // Key prop forces remount when session changes, triggering all cleanup automatically
  const sessionKey = sessionParam || "new";

  return <ChatScreen key={sessionKey} />;
}

export default withUnmountOnBlur(ChatWrapper);
